import "@/App.css";
import { useEffect, useState } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Landing from "@/pages/Landing";
import ComingSoon from "@/pages/ComingSoon";
import Terms from "@/pages/Terms";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import PortalIntake from "@/pages/PortalIntake";
import PortalSignup from "@/pages/PortalSignup";
import ParentLogin from "@/pages/ParentLogin";
import ParentDashboard from "@/pages/ParentDashboard";
import CheckInPage from "@/pages/CheckInPage";
import Careers from "@/pages/Careers";
import ApplicantApply from "@/pages/ApplicantApply";
import ApplicantLogin from "@/pages/ApplicantLogin";
import ApplicantDashboard from "@/pages/ApplicantDashboard";
import TrainingVideo from "@/pages/TrainingVideo";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ProtectedRoute from "@/components/ProtectedRoute";
import { AuthProvider } from "@/context/AuthContext";
import { ApplicantAuthProvider } from "@/context/ApplicantAuthContext";
import { Toaster } from "@/components/ui/sonner";

const LAUNCH_MODE = (process.env.REACT_APP_LAUNCH_MODE || "live").toLowerCase();
const IS_PREVIEW_MODE = LAUNCH_MODE === "preview";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function App() {
  // Once the launch date passes, we treat the app as LIVE regardless of REACT_APP_LAUNCH_MODE.
  const [launched, setLaunched] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/public/config`);
        if (!alive) return;
        if (data?.launch_date_iso) {
          const target = new Date(data.launch_date_iso).getTime();
          if (Number.isFinite(target) && Date.now() >= target) {
            setLaunched(true);
          }
        }
      } catch (_) {
        // treat as pre-launch by default
      } finally {
        if (alive) setConfigLoaded(true);
      }
    };
    check();
    const t = setInterval(check, 30000); // re-check every 30s so the site flips over automatically
    return () => { alive = false; clearInterval(t); };
  }, []);

  // While config is still loading and we're in preview mode, avoid a redirect flash by rendering a minimal splash.
  if (IS_PREVIEW_MODE && !configLoaded) {
    return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">Loading…</div>;
  }

  const isPreLaunch = IS_PREVIEW_MODE && !launched;
  const Gate = ({ children }) => (isPreLaunch ? <Navigate to="/coming-soon" replace /> : children);

  return (
    <div className="App" data-testid="app-root">
      <BrowserRouter>
        <AuthProvider>
          <ApplicantAuthProvider>
            <Routes>
              <Route path="/" element={isPreLaunch ? <ComingSoon /> : <Landing />} />
              <Route path="/coming-soon" element={isPreLaunch ? <ComingSoon /> : <Navigate to="/" replace />} />
              <Route path="/terms" element={<Terms />} />

              {/* Payment result pages (Stripe redirects here) */}
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />

              {/* Public marketing routes — gated pre-launch */}
              <Route path="/careers" element={<Gate><Careers /></Gate>} />
              <Route path="/careers/apply" element={<Gate><ApplicantApply /></Gate>} />
              <Route path="/careers/login" element={<Gate><ApplicantLogin /></Gate>} />
              <Route path="/careers/dashboard" element={<Gate><ApplicantDashboard /></Gate>} />
              <Route path="/careers/training/:module_id" element={<Gate><TrainingVideo /></Gate>} />
              <Route path="/portal/login" element={<Gate><ParentLogin /></Gate>} />
              <Route path="/portal" element={<Gate><ParentDashboard /></Gate>} />
              <Route path="/portal/signup" element={<Gate><PortalSignup /></Gate>} />
              <Route path="/portal/intake" element={<Gate><PortalIntake /></Gate>} />

              {/* Admin — always accessible (staff needs to run the app pre-launch) */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/checkin"
                element={
                  <ProtectedRoute>
                    <CheckInPage />
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ApplicantAuthProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
